import { PositioningPortal } from '@codastic/react-positioning-portal/lib'
import { useField, useFormikContext } from 'formik'
import { useRef, useState } from 'react'
import { SketchPicker } from 'react-color'

import type { ReactElement } from 'react'
import type { ColorChangeHandler, Color } from 'react-color'
import type { IPageContainerCreateUpdate } from '~/types/page-container'

import ActionInput from '../ActionInput'
import InputControls from '../Controls'

import { isValidHexColor } from '~/utils/string'

import './styles.scss'

interface Props {
  disabled?: boolean
  label?: string
  name: string
}

const ColorInput = ({ disabled, name }: Props): ReactElement => {
  const ref = useRef<SketchPicker>(null)

  const [, { error, value }, { setValue }] = useField<string>(name)

  const [colorValue, setColorValue] = useState<Color | string>(value)

  const { setFieldValue } = useFormikContext<IPageContainerCreateUpdate>()

  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false)

  const handleColorPickerButtonClick = () => setIsColorPickerOpen(!isColorPickerOpen)

  const handleClose = () => {
    setIsColorPickerOpen(false)
  }

  const handleColorChangeComplete: ColorChangeHandler = (color) => {
    const alpha = Math.round(Number(color.rgb.a) * 100)
    const transparency = alpha === 100 ? 'FF' : String(alpha).padStart(2, '0')
    setFieldValue(name, `${color.hex}${transparency}`)
    setColorValue(color.rgb)
  }

  const handlePaste = async () => {
    const value = await navigator.clipboard.readText()
    isValidHexColor(value) && setValue(value)
    setColorValue(value)
  }

  return (
    <div className="color-input-component">
      <PositioningPortal
        isOpen={isColorPickerOpen}
        onShouldClose={handleClose}
        portalContent={<SketchPicker color={colorValue} onChangeComplete={handleColorChangeComplete} ref={ref} />}
      >
        <div className="color-input-component__button">
          <ActionInput
            disabled={disabled}
            isValid={!!value && !error}
            label="color"
            onAddClick={handleColorPickerButtonClick}
            onInputClick={handleColorPickerButtonClick}
          >
            {value ? (
              <div className="color-input-component__button__color">
                <div className="color-input-component__button__color__preview" style={{ background: value }} />
                <span className="color-input-component__button__color__value">{value.toLocaleUpperCase()}</span>
              </div>
            ) : null}
          </ActionInput>
          {!!value ? <InputControls copyValue={value} onPaste={!disabled ? handlePaste : undefined} /> : null}
        </div>
      </PositioningPortal>
    </div>
  )
}

export default ColorInput
